import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../../../firebase';
import ClientLayout from '../../../components/layout/ClientLayout';

interface UserProfile {
  uid?: string;
  email: string;
  name: string;
  organization: string;
  phoneNumber: string;
  userLevel: number;
}

interface UserLevel {
  value: number;
  label: string;
}

const LevelUpRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    email: '',
    name: '',
    organization: '',
    phoneNumber: '',
    userLevel: 9
  });
  const [requestForm, setRequestForm] = useState({
    targetLevel: 8, // 기본값으로 항만근로자 설정
    reason: '',
    attachmentFile: null as File | null,
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // 신청 가능한 사용자 등급 목록
  const availableUserLevels: UserLevel[] = [
    { value: 8, label: '항만근로자' },
    { value: 7, label: '공공기관' },
    { value: 6, label: '입주협력업체' },
    { value: 5, label: '입주업체' },
    { value: 4, label: '센터 관계자' },
    { value: 3, label: '공사 관계자' },
  ];

  // 사용자 레벨에 해당하는 텍스트 반환 함수
  const getUserLevelText = (level: number): string => {
    switch (level) {
      case 1: return '최고 관리자';
      case 2: return '일반 관리자';
      case 3: return '공사 관계자';
      case 4: return '센터 관계자';
      case 5: return '입주업체';
      case 6: return '입주협력업체';
      case 7: return '공공기관';
      case 8: return '항만근로자';
      case 9: return '일반 사용자';
      default: return '알 수 없음';
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          navigate('/signin');
          return;
        }

        const userDocRef = doc(db, 'user_profiles', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data() as UserProfile;
          
          // 이미 일반 회원이 아닌 경우 설정 페이지로 리다이렉트
          if (data.userLevel !== 9) {
            navigate('/settings');
            return;
          }
          
          setUserProfile({
            ...data,
            uid: currentUser.uid
          });
        } else {
          setError('사용자 프로필을 찾을 수 없습니다.');
        }
      } catch (err) {
        console.error('프로필 로딩 오류:', err);
        setError('프로필 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // 파일 크기 체크 (5MB 제한)
      if (file.size > 5 * 1024 * 1024) {
        setError('첨부파일은 5MB 이하만 가능합니다.');
        return;
      }
      
      // 파일 형식 체크
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setError('PDF, JPEG 또는 PNG 파일만 업로드 가능합니다.');
        return;
      }
      
      setRequestForm({
        ...requestForm,
        attachmentFile: file
      });
      
      // 이미지 미리보기 생성
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!requestForm.reason.trim()) {
      setError('등업 신청 사유를 입력해주세요.');
      return;
    }
    
    try {
      setSubmitting(true);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/signin');
        return;
      }
      
      // 첨부 파일 업로드
      let attachmentUrl = '';
      if (requestForm.attachmentFile) {
        const fileRef = ref(
          storage, 
          `levelup_documents/${currentUser.uid}/${Date.now()}_${requestForm.attachmentFile.name}`
        );
        
        await uploadBytes(fileRef, requestForm.attachmentFile);
        attachmentUrl = await getDownloadURL(fileRef);
      }
      
      // Firestore에 등업 요청 저장
      const requestRef = doc(db, 'levelup_requests', currentUser.uid);
      await setDoc(requestRef, {
        uid: currentUser.uid,
        name: userProfile.name,
        email: userProfile.email,
        organization: userProfile.organization,
        phoneNumber: userProfile.phoneNumber,
        currentLevel: userProfile.userLevel,
        targetLevel: requestForm.targetLevel,
        reason: requestForm.reason,
        attachmentUrl: attachmentUrl,
        status: 'pending', // pending, approved, rejected
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      alert('등업 신청이 완료되었습니다. 관리자 승인 후 등급이 변경됩니다.');
      navigate('/settings');
      
    } catch (err) {
      console.error('등업 신청 오류:', err);
      setError('등업 신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/settings');
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">로딩중...</div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
          <h1 className="text-3xl font-bold text-center text-gray-900 mb-8">
            회원 등업 신청
          </h1>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">신청자 정보</label>
                <div className="mt-1 p-3 bg-gray-50 rounded-md">
                  <p><span className="font-medium">이름:</span> {userProfile.name}</p>
                  <p><span className="font-medium">이메일:</span> {userProfile.email}</p>
                  <p><span className="font-medium">소속:</span> {userProfile.organization || '정보 없음'}</p>
                  <p><span className="font-medium">연락처:</span> {userProfile.phoneNumber || '정보 없음'}</p>
                  <p><span className="font-medium">현재 등급:</span> {getUserLevelText(userProfile.userLevel)}</p>
                </div>
              </div>
              
              <div>
                <label htmlFor="targetLevel" className="block text-sm font-medium text-gray-700">
                  신청 등급
                </label>
                <select
                  id="targetLevel"
                  value={requestForm.targetLevel}
                  onChange={(e) => setRequestForm({...requestForm, targetLevel: parseInt(e.target.value)})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  {availableUserLevels.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  등업 신청은 관리자 검토 후 승인됩니다. 반드시 선택한 등급에 맞는 증빙 서류를 첨부해주세요.
                </p>
              </div>
              
              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                  비고
                </label>
                <textarea
                  id="reason"
                  value={requestForm.reason}
                  onChange={(e) => setRequestForm({...requestForm, reason: e.target.value})}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="기타 필요한 정보를 입력해 주세요."
                  required
                />
              </div>
              
              <div>
                <label htmlFor="attachment" className="block text-sm font-medium text-gray-700">
                  증빙 서류 (선택사항)
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  신분증, 재직증명서 등 신청 등급에 해당하는 증빙 서류를 첨부해주세요. (PDF, JPEG, PNG / 최대 5MB)
                </p>
                <input
                  type="file"
                  id="attachment"
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <div className="mt-1 flex items-center">
                  <label
                    htmlFor="attachment"
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 cursor-pointer"
                  >
                    파일 선택
                  </label>
                  <span className="ml-3 text-sm text-gray-500">
                    {requestForm.attachmentFile ? requestForm.attachmentFile.name : '선택된 파일 없음'}
                  </span>
                </div>
                
                {previewUrl && requestForm.attachmentFile?.type.startsWith('image/') && (
                  <div className="mt-3">
                    <img 
                      src={previewUrl} 
                      alt="미리보기" 
                      className="max-h-40 rounded-md border border-gray-300" 
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={submitting}
              >
                취소
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? '제출 중...' : '신청하기'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ClientLayout>
  );
};

export default LevelUpRequestPage; 